import React from 'react';
import { connect } from 'react-redux';
import { Chart } from 'react-google-charts';
import { DateTime, Duration } from 'luxon';

import withStyles from '@mui/styles/withStyles';

import { setTaskAccomplishmentDateRange } from '../store/slices/dashboardStatsSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';

import KSpinner from './KSpinner';
import DateRangePicker from './DateRangePicker';

const localStyles = {
  emptyDataDiv: {
    textAlign: 'center',
  },
  rangeChosingDiv: {
    textAlign: 'center',
  },
  selectDiv: {
    display: 'inline-block',
    width: '50%',
  },
  notificationDiv: {
    display: 'inline-block',
    width: '50%',
    fontSize: '120%',
    fontWeight: 500,
    color: '#cc0000',
    textAlign: 'center',
    height: '240px',
    marginTop: '130px',
  },
};

class TaskCompletionChart extends React.Component {
  combineClientsAndContacts = true;
  rawStats = [];

  constructor(props) {
    super(props);

    this.state = {
      dataReady: false,
      // shouldShowDateRangeChooser: false,
      useRangeName: true,
      timeRange: '',
      firstDate: null,
      lastDate: null,
    };
  }

  componentDidMount() {
    const { firstDate, lastDate } = this.determineBestTimeRange();
    this.setState({ firstDate, lastDate }, () => this.fetchRawTaskStats());
  }

  determineBestTimeRange() {
    const { firstDate, lastDate } = DA.getLastRequestedTasksAccomplishedTimeRange();
    if (!firstDate || !lastDate) {
      const startOfToday = DateTime.local().startOf('day');
      const sixDays = Duration.fromObject({ days: 6 });
      const sixDaysAgo = startOfToday.minus(sixDays);
      return { firstDate: sixDaysAgo, lastDate: startOfToday };
    } else {
      return { firstDate, lastDate };
    }
  }

  computeDatesFromTimeRangeName() {
    const now = DateTime.local();
    const startOfToday = now.startOf('day');

    switch (this.state.timeRange) {
      case 'All Time':
        return { firstDate: DateTime.fromMillis(0), lastDate: startOfToday };
      case 'This Week':
        const startOfWeek = now.startOf('week');
        return { firstDate: startOfWeek, lastDate: startOfToday };
      case 'Last Week':
        const oneWeek = Duration.fromObject({ weeks: 1 });
        const oneWeekAgo = now.minus(oneWeek);
        const startOfLastWeek = oneWeekAgo.startOf('week');
        const endOfLastWeek = oneWeekAgo.endOf('week').startOf('day');
        return { firstDate: startOfLastWeek, lastDate: endOfLastWeek };
      case 'Last Seven Days':
        const sixDays = Duration.fromObject({ days: 6 });
        const sixDaysAgo = startOfToday.minus(sixDays);
        return { firstDate: sixDaysAgo, lastDate: startOfToday };
      case 'This Month':
        const startOfMonth = now.startOf('month');
        return { firstDate: startOfMonth, lastDate: startOfToday };
      case 'Last Month':
        const oneMonthAgo = now.minus(Duration.fromObject({ months: 1 }));
        const startOfLastMonth = oneMonthAgo.startOf('month');
        const endOfLastMonth = oneMonthAgo.endOf('month').startOf('day');
        return { firstDate: startOfLastMonth, lastDate: endOfLastMonth };
      case 'Last 30 Days':
      default:
        const thirtyDaysAgo = startOfToday.minus(Duration.fromObject({ days: 29 }));
        return { firstDate: thirtyDaysAgo, lastDate: startOfToday };
    }
  }

  fetchRawTaskStats() {
    // const { firstDate, lastDate } = this.state.useRangeName ? this.computeDatesFromTimeRangeName() : this.state;
    const { firstDate, lastDate } = this.state;
    const firstDateStr = firstDate.toISODate();
    const lastDateStr = lastDate.toISODate();
    const uri = `/api/firm/userTasksAccomplished/start/${firstDateStr}/end/${lastDateStr}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      // this.props.addTaskAccomplishmentInfo({ timeRange: this.state.timeRange, results: json });
      this.props.setTaskAccomplishmentDateRange({ firstDate: firstDateStr, lastDate: lastDateStr });
      this.rawStats = this.convertJsonToRawStats(json);
      this.setState({ dataReady: true });
    };
    this.setState({ dataReady: false });
    apiFetch(this, request, actionOnGoodResponse);
  }

  /*
   * Users want to see this changing SOON after doing tasks, so don't use caching
  fetchRawTaskStats() {
    this.setState({ dataReady: false });
    const cachedResult = DA.getCachedTasksAccomplishedResultsForTimeRange(this.state.timeRange);
    if (cachedResult) {
      this.props.addTaskAccomplishmentInfo({ timeRange: this.state.timeRange, results: cachedResult });
      this.rawStats = this.convertJsonToRawStats(cachedResult);
      this.setState({ dataReady: true });
      return;
    }
    const { firstDate, lastDate } = this.state.useRangeName
      ? this.computeDatesFromTimeRangeName()
      : { firstDate: this.state.firstDate, lastDate: this.state.lastDate };
    const uri = `/api/firm/userTasksAccomplished/start/${firstDate.toISODate()}/end/${lastDate.toISODate()}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      this.props.addTaskAccomplishmentInfo({ timeRange: this.state.timeRange, results: json });
      this.rawStats = this.convertJsonToRawStats(json);
      this.setState({ dataReady: true });
    };
    this.setState({ dataReady: false });
    apiFetch(this, request, actionOnGoodResponse);
  }
  */

  convertJsonToRawStats(json) {
    return json;
  }

  computeDataFromRawStats() {
    const firstRow = [
      'User & Domain',
      'Phone Calls',
      'Voice Mail',
      'Email',
      'Text',
      'Meeting',
      'Tour',
      'Schedule Assessment',
      'Complete Assessment',
      'Note',
    ];
    let rows = [firstRow];
    const names = this.getNamesFromRawStats();
    names.forEach((name) => {
      if (this.combineClientsAndContacts) {
        const combinedRow = this.computeCombinedRowForName(name);
        rows = [...rows, combinedRow];
      } else {
        const prospectRow = this.computeProspectRowForName(name);
        const contactRow = this.computeContactRowForName(name);
        rows = [...rows, prospectRow, contactRow];
      }
    });
    return rows;

    /*
    return [
      firstRow,
      ['Matt: Prospects', 10, 24, 20, 32, 18, 18],
      ['Contacts', 10, 24, 20, 32, 18, 18],
      ['Aaron: Prospects', 16, 22, 23, 30, 16, 18],
      ['Contacts', 16, 22, 23, 30, 16, 18],
      ['Steve: Prospects', 28, 19, 29, 30, 12, 18],
      ['Contacts', 28, 19, 29, 30, 12, 18]
    ];
    */
  }

  getNamesFromRawStats() {
    return this.rawStats.map((obj) => obj.personName);
  }

  computeProspectRowForName(name) {
    const personStatsObj = this.rawStats.find((stats) => stats.personName === name);
    const personStatsArray = personStatsObj.done;
    if (name.length > 5) {
      name = name.substring(0, 5);
    }
    return [
      name + ': Prospects',
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Phone Call').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Voice Mail').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Email').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Text').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Meeting').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Tour').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Schedule Assessment').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Complete Assessment').count,
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Note').count,
    ];
  }

  computeContactRowForName(name) {
    const personStatsObj = this.rawStats.find((stats) => stats.personName === name);
    const personStatsArray = personStatsObj.done;
    return [
      'Contacts',
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Phone Call').count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Voice Mail').count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Email').count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Text').count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Meeting').count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Tour').count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Schedule Assessment')
        .count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Complete Assessment')
        .count,
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Note').count,
    ];
  }

  computeCombinedRowForName(name) {
    const personStatsObj = this.rawStats.find((stats) => stats.personName === name);
    const personStatsArray = personStatsObj.done;
    if (name.length > 12) {
      name = name.substring(0, 12);
    }
    const phoneCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Phone Call').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Phone Call').count;
    const voiceCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Voice Mail').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Voice Mail').count;
    const emailCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Email').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Email').count;
    const textCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Text').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Text').count;
    const meetingCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Meeting').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Meeting').count;
    const tourCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Tour').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Tour').count;
    const assessmentScheduledCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Schedule Assessment')
        .count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Schedule Assessment')
        .count;
    const assessmentCompletedCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Complete Assessment')
        .count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Complete Assessment')
        .count;
    const noteCount =
      personStatsArray.find((entry) => entry.targetType === 'Client' && entry.activity === 'Note').count +
      personStatsArray.find((entry) => entry.targetType === 'Contact' && entry.activity === 'Note').count;
    return [
      name + ':',
      phoneCount,
      voiceCount,
      emailCount,
      textCount,
      meetingCount,
      tourCount,
      assessmentScheduledCount,
      assessmentCompletedCount,
      noteCount,
    ];
  }

  /*
   * We're no longer using 'named' ranges
  setTimeRange(event) {
    const newRange = event.target.value;
    if (newRange === 'Other') {
      const { firstDate, lastDate } = this.computeDatesFromTimeRangeName();
      this.setState({ useRangeName: false, shouldShowDateRangeChooser: true, firstDate, lastDate });
    } else {
      this.setState({ useRangeName: true, timeRange: newRange }, () => this.fetchRawTaskStats());
    }
  }
  */

  setFirstDate(newDate) {
    this.setState({ firstDate: newDate }, () => this.fetchRawTaskStats());
  }

  setLastDate(newDate) {
    this.setState({ lastDate: newDate }, () => this.fetchRawTaskStats());
  }

  render() {
    if (!this.state.dataReady) {
      return <KSpinner />;
    }

    const { classes } = this.props;
    const data = this.computeDataFromRawStats();
    const haveData = data.length > 1;

    const taskCompletionOptions = {
      legend: { position: 'top', maxLines: 4 },
      bar: { groupWidth: '60%' },
      isStacked: true,
    };

    return (
      <div>
        {haveData ? (
          <Chart
            chartType="BarChart"
            width={'100%'}
            height={'370px'}
            loader={<KSpinner />}
            data={data}
            options={taskCompletionOptions}
          />
        ) : (
          <div className={classes.emptyDataDiv}>
            <div className={classes.notificationDiv}>No data in time range</div>
          </div>
        )}
        <div className={classes.rangeChosingDiv}>
          <DateRangePicker
            startLabel="Start Date"
            endLabel="End Date"
            startValue={this.state.firstDate}
            endValue={this.state.lastDate}
            onStartValueChange={(newDate) => this.setFirstDate(newDate)}
            onEndValueChange={(newDate) => this.setLastDate(newDate)}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

const actionProducers = {
  setTaskAccomplishmentDateRange,
};

export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(TaskCompletionChart));

/*
 * When we showed named ranges we had this code:
        <div className={classes.rangeChosingDiv}>
          {this.state.shouldShowDateRangeChooser ? (
            <DateRangePicker
              startLabel="First Date"
              endLabel="Last Date"
              startValue={this.state.firstDate}
              endValue={this.state.lastDate}
              onStartValueChange={(newDate) => this.setFirstDate(newDate)}
              onEndValueChange={(newDate) => this.setLastDate(newDate)}
            />
          ) : (
            <div>
              <div className={classes.selectDiv}>
                <KSelect
                  fullWidth={true}
                  label="Time Range"
                  liveOptions={[
                    'Other',
                    'All Time',
                    'This Week',
                    'Last Week',
                    'Last Seven Days',
                    'This Month',
                    'Last Month',
                    'Last 30 Days',
                  ]}
                  value={this.state.timeRange}
                  onChange={(event) => this.setTimeRange(event)}
                />
              </div>
            </div>
          )}
        </div>
 */
